import React, { Suspense } from "react";
import { Route, Switch } from "react-router-dom";
import { Layout } from "./components/layout/Layout";
import { CircularProgress, Box } from "@mui/material";
// Eager-loaded components
import HomePage from "./components/pages/HomePage";
import LoginPage from "./components/pages/LoginPage";

// Loading fallback component
const LoadingFallback = () => (
  <Box
    display="flex"
    justifyContent="center"
    alignItems="center"
    minHeight="400px"
  >
    <CircularProgress />
  </Box>
);

// Enum for route paths
export enum RoutePaths {
  HOME = "/home",
  LOGIN = "/login",
  SET_PASSWORD = "/set-password/:setupHash",
  SELECT_VENUE = "/select-venue",
  CREATE_NEW_INCIDENT = "/create-new-incident",
  CASES = "/cases",
  CASE_DETAILS = "/case-details/:helpRequestId",
  SEARCH_USER = "/search-user",
  USER_CASE_DETAILS = "/user-case-details/:userId",
  USER_CASE_FULL_DETAILS = "/user-case-details/:userId/full-details/:helpRequestId",
  SETTINGS = "/settings",
  VENUE_DEVICES = "/venue-devices",
  ALERT_RECEIVERS = "/alert-receivers",
  STAFF_RESPONDER_ACCOUNTS = "/staff-responder-accounts",
  STAFF_USER_ACCOUNTS = "/staff-user-accounts",
  INCIDENT_REASONS = "/incident-reasons",
  CUSTOM_ACTIONS = "/custom-actions",
  SITE_LOCATIONS = "/site-locations",
  ADMIN_VENUES = "/admin/venues",
  ADMIN_COMMAND_CENTRE_USERS = "/admin/command-centre-users",
  ADMIN_GEO_SAFETY = "/admin/geo-safety",
  ADMIN_GEO_CREATOR = "/admin/geo-creator",
  ADMIN_VENUE_SETUP_STATUS = "/admin/venue-setup-status",
  ADMIN_INCIDENT_APP_DEVICE_KEYS = "/admin/incident-app-device-keys",
  ADMIN_RESPONDER_APP_DEVICE_KEYS = "/admin/responder-app-device-keys",
  ANALYTICS = "/analytics",
  REPORTING = "/reporting",
  GAMING_INCIDENT_REGISTER = "/gaming-incident-register",
  SMS_CONFIGURATION = "/sms-configuration",
  DEFAULT = "/",
}

// Lazy-loaded components
const SelectVenuePage = React.lazy(
  () => import("./components/pages/select-venue")
);
const LoginSetupPage = React.lazy(
  () => import("./components/pages/LoginSetupPage")
);
const CreateIncidentPage = React.lazy(
  () => import("./components/pages/CreateIncident/CreateIncidentPage")
);
const CasesPage = React.lazy(
  () => import("./components/pages/cases/CasesPage")
);
const CaseDetailsPage = React.lazy(
  () => import("./components/pages/cases/CaseDetailsPage")
);
const SearchCaseDetailsPage = React.lazy(
  () => import("./components/pages/cases/SearchCaseDetailsPage")
);
const UserSearchPage = React.lazy(
  () => import("./components/pages/UserSearchAndDetails/UserSearchPage")
);
const UserDetailsPage = React.lazy(
  () => import("./components/pages/UserSearchAndDetails/UserDetailsPage")
);
const SettingsPage = React.lazy(
  () => import("./components/pages/venue-setup/SettingsPage")
);
const VenueDevicesPage = React.lazy(
  () => import("./components/pages/venue-setup/VenueDevicesPage")
);
const AlertReceiverSetupPage = React.lazy(
  () =>
    import(
      "./components/pages/venue-setup/AlertReceiverSetup/AlertReceiverSetupPage"
    )
);
const StaffResponderAccountsPage = React.lazy(
  () => import("./components/pages/venue-setup/StaffResponderAccountsPage")
);
const StaffUserAccountsPage = React.lazy(
  () => import("./components/pages/venue-setup/StaffUserAccountsPage")
);
const IncidentReasonsPage = React.lazy(
  () => import("./components/pages/venue-setup/IncidentReasonsPage")
);
const SiteLocationsPage = React.lazy(
  () => import("./components/pages/venue-setup/SiteLocationsPage")
);
const CustomActionsPage = React.lazy(
  () => import("./components/pages/venue-setup/CustomActionsPage")
);
const VenuesSetupAdminPage = React.lazy(
  () => import("./components/pages/admin/VenuesSetupAdminPage")
);
const CcAccountsAdminPage = React.lazy(
  () => import("./components/pages/admin/CcAccountsAdminPage")
);
const IncidentAppVenueDeviceKeysAdminPage = React.lazy(
  () => import("./components/pages/admin/IncidentAppVenueDeviceKeysAdminPage")
);
const ResponderAppVenueDeviceKeysAdminPage = React.lazy(
  () => import("./components/pages/admin/ResponderAppVenueDeviceKeysAdminPage")
);
const VenueSetupStatusPage = React.lazy(
  () => import("./components/pages/venue-setup-status-page")
);
const GeoSafetyAdminPage = React.lazy(
  () => import("./components/pages/geo-safety/GeoSafetyAdminPage")
);
const GeoCreatorPage = React.lazy(
  () => import("./components/pages/geo-safety/GeoCreatorPage")
);
const AnalyticsPage = React.lazy(
  () => import("./components/pages/analyticsreports/AnalyticsPage")
);
const ReportingPage = React.lazy(
  () => import("./components/pages/analyticsreports/reporting/ReportingPage")
);
const GamingIncidentRegisterPage = React.lazy(
  () => import("./components/pages/analyticsreports/GamingIncidentRegisterPage")
);
const SmsPage = React.lazy(() => import("./components/pages/sms/SmsPage"));

// Route configuration
const routes = [
  { path: RoutePaths.HOME, component: HomePage, exact: true },
  { path: RoutePaths.LOGIN, component: LoginPage, exact: true },
  { path: RoutePaths.SET_PASSWORD, component: LoginSetupPage, exact: true },
  { path: RoutePaths.SELECT_VENUE, component: SelectVenuePage, exact: true },
  {
    path: RoutePaths.CREATE_NEW_INCIDENT,
    component: CreateIncidentPage,
    exact: true,
  },
  { path: RoutePaths.CASES, component: CasesPage, exact: true },
  { path: RoutePaths.CASE_DETAILS, component: CaseDetailsPage, exact: true },
  { path: RoutePaths.SEARCH_USER, component: UserSearchPage, exact: true },
  {
    path: RoutePaths.USER_CASE_DETAILS,
    component: UserDetailsPage,
    exact: true,
  },
  {
    path: RoutePaths.USER_CASE_FULL_DETAILS,
    component: SearchCaseDetailsPage,
    exact: true,
  },
  { path: RoutePaths.SETTINGS, component: SettingsPage, exact: true },
  { path: RoutePaths.VENUE_DEVICES, component: VenueDevicesPage, exact: true },
  {
    path: RoutePaths.ALERT_RECEIVERS,
    component: AlertReceiverSetupPage,
    exact: true,
  },
  {
    path: RoutePaths.STAFF_RESPONDER_ACCOUNTS,
    component: StaffResponderAccountsPage,
    exact: true,
  },
  {
    path: RoutePaths.STAFF_USER_ACCOUNTS,
    component: StaffUserAccountsPage,
    exact: true,
  },
  {
    path: RoutePaths.INCIDENT_REASONS,
    component: IncidentReasonsPage,
    exact: true,
  },
  {
    path: RoutePaths.CUSTOM_ACTIONS,
    component: CustomActionsPage,
    exact: true,
  },
  {
    path: RoutePaths.SITE_LOCATIONS,
    component: SiteLocationsPage,
    exact: true,
  },
  {
    path: RoutePaths.ADMIN_VENUES,
    component: VenuesSetupAdminPage,
    exact: true,
  },
  {
    path: RoutePaths.ADMIN_COMMAND_CENTRE_USERS,
    component: CcAccountsAdminPage,
    exact: true,
  },
  {
    path: RoutePaths.ADMIN_GEO_SAFETY,
    component: GeoSafetyAdminPage,
    exact: true,
  },
  {
    path: RoutePaths.ADMIN_GEO_CREATOR,
    component: GeoCreatorPage,
    exact: true,
  },
  {
    path: RoutePaths.ADMIN_VENUE_SETUP_STATUS,
    component: VenueSetupStatusPage,
    exact: true,
  },
  {
    path: RoutePaths.ADMIN_INCIDENT_APP_DEVICE_KEYS,
    component: IncidentAppVenueDeviceKeysAdminPage,
    exact: true,
  },
  {
    path: RoutePaths.ADMIN_RESPONDER_APP_DEVICE_KEYS,
    component: ResponderAppVenueDeviceKeysAdminPage,
    exact: true,
  },
  { path: RoutePaths.ANALYTICS, component: AnalyticsPage, exact: true },
  { path: RoutePaths.REPORTING, component: ReportingPage, exact: true },
  {
    path: RoutePaths.GAMING_INCIDENT_REGISTER,
    component: GamingIncidentRegisterPage,
    exact: true,
  },
  { path: RoutePaths.SMS_CONFIGURATION, component: SmsPage, exact: true },
  { path: RoutePaths.DEFAULT, component: HomePage, exact: false },
];

export const App = () => {
  return (
    <Layout>
      <Suspense fallback={<LoadingFallback />}>
        <Switch>
          {routes.map(({ path, component: Component, exact }, index) => (
            <Route
              key={index}
              path={path}
              exact={exact}
              component={Component}
            />
          ))}
        </Switch>
      </Suspense>
    </Layout>
  );
};
